import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Users, UserPlus, MessageCircle, Zap } from 'lucide-react';
import { apiClient } from '../../api/client';

const LiveActivityPulse = () => {
  const [pulse, setPulse] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchPulse = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/admin/pulse/live');
      setPulse(data);
    } catch {
      // silently fail — non-critical
    }
  }, []);

  useEffect(() => {
    fetchPulse();
    const interval = setInterval(fetchPulse, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchPulse]);

  if (!pulse) return null;

  return (
    <div className="relative" data-testid="live-activity-pulse">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-green-50 border border-transparent hover:border-green-200"
        data-testid="pulse-toggle"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
        </span>
        <span className="hidden sm:inline text-green-700">{pulse.active_now} live</span>
        <span className="sm:hidden text-green-700">{pulse.active_now}</span>
      </button>

      {expanded && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setExpanded(false)} />
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-20 overflow-hidden" data-testid="pulse-dropdown">
            <div className="px-4 py-3 border-b border-gray-100" style={{ backgroundColor: '#FAFAF8' }}>
              <div className="flex items-center gap-2 text-sm font-bold" style={{ color: '#1A1209' }}>
                <Activity size={14} style={{ color: '#F5831F' }} />
                Live Platform Pulse
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-100">
              <div className="bg-white p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Users size={12} className="text-green-500" />
                </div>
                <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{pulse.active_now}</p>
                <p className="text-[10px] text-gray-500">Active Now</p>
              </div>
              <div className="bg-white p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <UserPlus size={12} className="text-blue-500" />
                </div>
                <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{pulse.signups_today}</p>
                <p className="text-[10px] text-gray-500">Signups Today</p>
              </div>
              <div className="bg-white p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <MessageCircle size={12} className="text-purple-500" />
                </div>
                <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{pulse.conversations_today}</p>
                <p className="text-[10px] text-gray-500">Convos Today</p>
              </div>
              <div className="bg-white p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Zap size={12} className="text-amber-500" />
                </div>
                <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{pulse.conversations_hour}</p>
                <p className="text-[10px] text-gray-500">Last Hour</p>
              </div>
            </div>

            {pulse.recent_events?.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Recent Activity</p>
                {pulse.recent_events.slice(0, 4).map((evt, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 text-xs text-gray-600">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${evt.type === 'signup' ? 'bg-blue-400' : 'bg-purple-400'}`} />
                    <span className="truncate">{evt.text}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="px-3 py-2 border-t border-gray-100 text-center">
              <p className="text-[10px] text-gray-400">{pulse.total_users?.toLocaleString()} total users</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LiveActivityPulse;
